import { useNavigation } from '@react-navigation/native';
import { OPEN_MENU } from '@reduxLocal/actions/types';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

/** Parent LoginStack routes that must not be reached via in-app back. */
const AUTH_STACK_EXIT_ROUTES = new Set([
  'LoadingScreen',
  'Loading',
  'Login',
  'Registration',
  'Auth',
  'Learn',
]);

/**
 * True when the nearest navigator that can go back would land on an in-app
 * screen — not LoadingScreen / Login on the parent auth stack.
 *
 * `navigation.canGoBack()` is too broad: after LoadingScreen → Dashboard it
 * stays true at the LoginStack level, so a raw `goBack()` leaves the dashboard.
 */
export function canSafelyGoBack(navigation: any): boolean {
  let nav = navigation;
  while (nav) {
    const state = nav.getState?.();
    if (state && typeof state.index === 'number' && state.index > 0) {
      const previous = state.routes?.[state.index - 1];
      if (previous?.name && AUTH_STACK_EXIT_ROUTES.has(String(previous.name))) {
        return false;
      }
      return true;
    }
    nav = nav.getParent?.();
  }
  return false;
}

/** Go back only when safe; otherwise run fallback (e.g. open menu). */
export function safeGoBack(navigation: any, onFallback?: () => void): boolean {
  if (canSafelyGoBack(navigation)) {
    navigation.goBack();
    return true;
  }
  onFallback?.();
  return false;
}

export const useBackButtonCallback = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();

  return useCallback(() => {
    try {
      safeGoBack(navigation, () => {
        dispatch({ type: OPEN_MENU });
      });
    } catch (error) {
      console.warn('[BackButton] Navigation failed, opening menu fallback', error);
      dispatch({ type: OPEN_MENU });
    }
  }, [navigation, dispatch]);
};
